import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause, Square, CheckCircle2, Circle, Sparkles, KeyRound, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

function liveSeconds(session) {
  if (!session?.started_at) return 0;
  const start = new Date(session.started_at).getTime();
  const end = session.ended_at ? new Date(session.ended_at).getTime() : Date.now();
  let brk = 0;
  for (const b of session.breaks || []) {
    if (!b.start) continue;
    const bs = new Date(b.start).getTime();
    const be = b.end ? new Date(b.end).getTime() : end;
    brk += Math.max(0, (be - bs) / 1000);
  }
  return Math.max(0, Math.floor((end - start) / 1000 - brk));
}

function hms(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export default function StripTaskCard({ clean, onStart, onPause, onResume, onEnd, onToggleRoom, onKeyReturnRoom }) {
  const { t } = useTranslation();
  const status = clean.session?.status || "pending";
  const running = status === "in_progress";
  const paused = status === "paused";
  const completed = status === "completed";
  const rooms = (clean.checklist || []).filter((i) => i.is_strip_room);
  const doneCount = rooms.filter((r) => r.checked).length;
  const allDone = rooms.length > 0 && doneCount === rooms.length;

  const [secs, setSecs] = useState(liveSeconds(clean.session));
  useEffect(() => {
    setSecs(liveSeconds(clean.session));
    if (!running) return;
    const id = setInterval(() => setSecs(liveSeconds(clean.session)), 1000);
    return () => clearInterval(id);
  }, [running, clean.session]);

  return (
    <div
      id={`rc-${clean.id}`}
      data-testid="strip-task-card"
      className="rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-600" />
          <div>
            <div className="font-display text-lg font-extrabold text-amber-900">{t("Strip rooms")}</div>
            <div className="text-xs font-semibold text-amber-700">
              {doneCount}/{rooms.length} {t("stripped")}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-3xl font-extrabold tabular-nums text-amber-900" data-testid="strip-task-timer">
            {hms(secs)}
          </div>
          <div className="label-caps text-[10px] text-amber-700">
            {running ? t("running") : paused ? t("paused") : completed ? t("total time") : t("not started")}
          </div>
        </div>
      </div>

      {/* room checklist */}
      {status !== "completed" && (
        <div className="mt-3 space-y-1.5" data-testid="strip-task-rooms">
          {rooms.length === 0 && (
            <p className="text-sm text-muted-foreground">{t("No rooms need stripping right now.")}</p>
          )}
          {rooms.map((r) => (
            r.checked || r.key_returned ? (
              <div
                key={r.id}
                data-testid={`strip-task-room-wrap-${r.room_number}`}
                className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 ${
                  r.checked ? "border-emerald-200 bg-emerald-50" : "border-sky-200 bg-sky-50"
                }`}
              >
                <button
                  type="button"
                  onClick={() => onToggleRoom(clean, r.room_clean_id, !r.checked)}
                  data-testid={`strip-task-room-${r.room_number}`}
                  className="k-press flex flex-1 items-center gap-3 text-left"
                >
                  {r.checked ? (
                    <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                  ) : (
                    <Circle className="h-5 w-5 shrink-0 text-muted-foreground" />
                  )}
                  <span className={`text-lg font-bold ${r.checked ? "text-emerald-700 line-through" : "text-foreground"}`}>
                    {t("Room")} {r.room_number}
                  </span>
                </button>
                {!r.checked && (
                  <button
                    type="button"
                    onClick={() => onKeyReturnRoom(clean, r.room_clean_id, false)}
                    data-testid={`strip-task-undo-key-${r.room_number}`}
                    className="k-press flex shrink-0 items-center gap-1 text-xs font-semibold text-muted-foreground underline hover:text-foreground"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> {t("Undo key")}
                  </button>
                )}
              </div>
            ) : (
              <div
                key={r.id}
                data-testid={`strip-task-room-wrap-${r.room_number}`}
                className="flex w-full items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2"
              >
                <span className="flex-1 text-lg font-bold text-foreground">{t("Room")} {r.room_number}</span>
                <Button
                  size="sm"
                  onClick={() => onKeyReturnRoom(clean, r.room_clean_id, true)}
                  data-testid={`strip-task-key-return-${r.room_number}`}
                  className="k-press h-9 shrink-0 gap-1.5 bg-sky-600 text-xs font-bold hover:bg-sky-700"
                >
                  <KeyRound className="h-4 w-4" /> {t("Key Returned")}
                </Button>
              </div>
            )
          ))}
        </div>
      )}

      {/* controls */}
      <div className="mt-4 flex gap-2">
        {status === "pending" && (
          <Button
            onClick={() => onStart(clean)}
            data-testid="strip-task-start"
            className="k-press h-12 w-full gap-2 text-base font-bold"
          >
            <Play className="h-5 w-5" /> {t("Start stripping")}
          </Button>
        )}

        {running && (
          <>
            {allDone ? (
              <Button
                onClick={() => onEnd(clean)}
                data-testid="strip-task-end"
                className="k-press h-12 w-full gap-2 bg-emerald-600 text-base font-bold hover:bg-emerald-700"
              >
                <Square className="h-5 w-5" /> {t("End task")}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => onPause(clean)}
                  data-testid="strip-task-pause"
                  variant="outline"
                  className="k-press h-12 flex-1 gap-2 border-amber-400 text-base font-bold text-amber-800"
                >
                  <Pause className="h-5 w-5" /> {t("Pause task")}
                </Button>
                <Button
                  onClick={() => onEnd(clean)}
                  data-testid="strip-task-end"
                  variant="outline"
                  className="k-press h-12 flex-1 gap-2 text-base font-bold"
                >
                  <Square className="h-5 w-5" /> {t("End task")}
                </Button>
              </>
            )}
          </>
        )}

        {paused && (
          <Button
            onClick={() => onResume(clean)}
            data-testid="strip-task-resume"
            className="k-press h-12 w-full gap-2 text-base font-bold"
          >
            <Play className="h-5 w-5" /> {t("Resume stripping")}
          </Button>
        )}

        {completed && (
          <div
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-100 py-3 text-base font-bold text-emerald-700"
            data-testid="strip-task-done"
          >
            <CheckCircle2 className="h-5 w-5" /> {t("Stripping complete")}
          </div>
        )}
      </div>
    </div>
  );
}
